/* eslint-disable no-debugger */
import { Component, h, Prop, Watch, Host, Element, Listen, State, Event, EventEmitter } from '@stencil/core';
import { isMagnoliaEditingMode } from '../../helper';
import { HeaderHeightChangeEvent } from '../framework/ui-cms-header/ui-cms-header';

@Component({
  tag: 'ui-cms-anchor',
  styleUrls: {
    default: 'ui-cms-anchor.scss',
    editing: 'ui-cms-anchor.magnolia.scss',
  },
  shadow: true,
})
export class UiCmsAnchor {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) i18n = { editorInfoMessageAnchor: 'Sprungmarke' };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @State() scrollToTopValue = 0;

  @Watch('scrollToTopValue')
  onScrollToTopValueChange(): void {
    this.setupScrollAnimation();
  }

  private scrollOffset = 0;

  @Listen('uiCmsHeaderHeightChanged', { target: 'window' })
  headerHeightChangedHandler(e: CustomEvent<HeaderHeightChangeEvent>): void {
    this.setScrollOffset(e.detail.height);
  }

  @Event({ eventName: 'anchorCall' }) anchorCallEvent: EventEmitter<{ name: string }>;

  /** Listen to anchorCall event emitted on ui-cms-link click and scroll to the anchor */
  @Listen('anchorCall', { target: 'window' })
  anchorCallHandler(e: CustomEvent<{ name: string }>): void {
    if (e.detail.name === this.el.id) {
      this.setupScrollAnimation();
      this.recheckScroll();
    }
  }

  componentWillLoad() {
    this.utilityI18nChangeHandler(this.utilityI18n);

    // Set initial scroll offset to header height
    const headerHeight = this.getHeaderHeight();
    this.setScrollOffset(headerHeight);
  }

  componentDidLoad() {
    this.handleAnchorInUrl();
  }

  private setScrollOffset(offset: number): void {
    this.scrollOffset = offset;
    this.el.style.setProperty('--scroll-offset', `${offset}px`);
  }

  private isAnchorInUrl(): boolean {
    const hash = window.location.hash.split('#')[1];
    return hash && hash === this.el.id;
  }

  private handleAnchorInUrl(): void {
    if (this.isAnchorInUrl()) {
      this.scrollToMe();
    }
  }

  private scrollToMe(): void {
    this.anchorCallEvent.emit({ name: this.el.id });
    // If content is already loaded trigger scroll animation, otherwise wait until loaded
    if (document.readyState === 'complete') {
      this.setupScrollAnimation();
      this.recheckScroll();
    } else {
      window.onload = () => {
        this.setupScrollAnimation();
        this.recheckScroll();
      };
    }
  }

  /**
   * gets triggered multiple times because of the scrollToTopValue watcher.
   * This is a workaround to get the correct scroll position.
   * Without this workaround the scroll position is not correct because
   * of multiple layout shifts on page load.
   */
  private setupScrollAnimation(): void {
    const { top } = this.el.getBoundingClientRect();

    window.scrollTo({
      top: top - this.scrollOffset + window.scrollY,
      left: 0,
    });
  }

  private recheckScroll(): void {
    let scrollToTopValue = 0;
    const interval = setInterval(() => {
      const { top } = this.el.getBoundingClientRect();

      if (scrollToTopValue !== top) {
        scrollToTopValue = top;
      } else {
        this.scrollToTopValue = scrollToTopValue;
        clearInterval(interval);
      }
    }, 200);
  }

  private getHeaderHeight(): number {
    const isB2b = window?.ui?.cmsState?.isB2b;
    const header = document.querySelector('ui-cms-header');
    let headerHeight = 0;

    if (isB2b) {
      headerHeight = 70;
    } else {
      headerHeight = header?.shadowRoot?.querySelector('sd-header')?.clientHeight || 0;
    }

    return headerHeight;
  }

  render() {
    return (
      <Host>
        {isMagnoliaEditingMode() && (
          <span>
            <ui-icon icon="anchor-link" type="system"></ui-icon>
            {this.i18n.editorInfoMessageAnchor}: {this.el.id}
          </span>
        )}
      </Host>
    );
  }
}
